import {
	getLivestockAttentionAnimalCountsQueryOptions,
	getLivestockAttentionQueryOptions,
} from "@packages/api-client/hooks/livestockController";
import type {
	GetLivestockAttention200,
	GetLivestockAttentionAnimalCounts200,
} from "@packages/api-client/types/livestockController";
import type { TFunction } from "i18next";
import {
	Baby,
	type LucideIcon,
	MapPin,
	Scale,
	Syringe,
	Users,
} from "lucide-react";
import type { FarmScope } from "@/hooks/useFarmScope";

export type LivestockAttentionResponse = GetLivestockAttention200;
export type LivestockAttentionItem =
	LivestockAttentionResponse["items"][number];
export type LivestockAttentionCategory =
	keyof LivestockAttentionResponse["summary"];

/**
 * Narrowed variants of the generated discriminated union — the item shapes
 * a consumer holds after narrowing on `category` (+ `entityType`), with the
 * per-category `detail` and typed `action.prefill` attached.
 */
export type VaccinationHerdAttentionItem = Extract<
	LivestockAttentionItem,
	{ category: "VACCINATION_DUE"; entityType: "herd" }
>;
export type PaddockOverstayAttentionItem = Extract<
	LivestockAttentionItem,
	{ category: "PADDOCK_OVERSTAY" }
>;

/**
 * One shared query definition for every attention consumer (hub strip, herd
 * badges) so React Query dedupes them into a single request per scope.
 * limitPerCategory matches the backend max — the strip is the "see a lot"
 * surface; exact totals always ride on `summary`.
 */
const LIVESTOCK_ATTENTION_LIMIT = 50;

export const livestockAttentionQuery = (orgId: string, scope: FarmScope) =>
	getLivestockAttentionQueryOptions(orgId, {
		establishmentId:
			scope.kind === "single" ? scope.establishmentId : undefined,
		limitPerCategory: LIVESTOCK_ATTENTION_LIMIT,
	});

/**
 * Herd-scoped attention (fact-grain redesign): the backend returns exact herd
 * slices — vaccination/weighing aggregate rows carry the overdue `animalIds`
 * (the bulk-action preselect set), and per-animal categories (calving) that
 * belong to the herd. No cap artifacts. `categories` narrows the read (e.g.
 * `["VACCINATION_DUE"]` for the bulk-action id resolution).
 */
export const livestockAttentionForHerdQuery = (
	orgId: string,
	scope: FarmScope,
	herdId: string,
	categories?: LivestockAttentionCategory[],
) =>
	getLivestockAttentionQueryOptions(orgId, {
		establishmentId:
			scope.kind === "single" ? scope.establishmentId : undefined,
		limitPerCategory: LIVESTOCK_ATTENTION_LIMIT,
		herdId,
		...(categories && categories.length > 0
			? { categories: categories.join(",") }
			: {}),
	});

/**
 * Animal-scoped attention: the animal's own per-animal items across every
 * category (its overdue vaccinations, weighing, calving) — the fix for "herd
 * says vaccination missing, animal page silent".
 */
export const livestockAttentionForAnimalQuery = (
	orgId: string,
	scope: FarmScope,
	animalId: string,
) =>
	getLivestockAttentionQueryOptions(orgId, {
		establishmentId:
			scope.kind === "single" ? scope.establishmentId : undefined,
		limitPerCategory: LIVESTOCK_ATTENTION_LIMIT,
		animalId,
	});

export type LivestockAttentionCountsResponse =
	GetLivestockAttentionAnimalCounts200;

/**
 * Per-animal attention totals for the list/card ⚠ badges — exact per page (the
 * ids of the current page), not derived from the capped org feed. Only animals
 * with ≥1 item come back, so a missing id is "clean".
 */
export const livestockAttentionCountsQuery = (
	orgId: string,
	scope: FarmScope,
	animalIds: string[],
) =>
	getLivestockAttentionAnimalCountsQueryOptions(orgId, {
		establishmentId:
			scope.kind === "single" ? scope.establishmentId : undefined,
		animalIds: animalIds.join(","),
	});

/** Collapse the per-animal counts response into a total-per-animal map. */
export function attentionCountsToMap(
	counts: LivestockAttentionCountsResponse | undefined,
): Map<string, number> {
	const byAnimal = new Map<string, number>();
	for (const [animalId, summary] of Object.entries(counts ?? {})) {
		const total = Object.values(summary).reduce((sum, n) => sum + n, 0);
		if (total > 0) byAnimal.set(animalId, total);
	}
	return byAnimal;
}

/** Canonical category order + icon; labels live in the i18n catalog. */
export const LIVESTOCK_ATTENTION_CATEGORIES: Array<{
	key: LivestockAttentionCategory;
	icon: LucideIcon;
}> = [
	{ key: "VACCINATION_DUE", icon: Syringe },
	{ key: "WEIGHING_OVERDUE", icon: Scale },
	{ key: "PADDOCK_OVERSTAY", icon: MapPin },
	{ key: "OVER_CAPACITY", icon: Users },
	{ key: "CALVING_WINDOW", icon: Baby },
];

export function attentionTotal(
	summary: LivestockAttentionResponse["summary"] | undefined,
): number {
	if (!summary) return 0;
	return Object.values(summary).reduce((sum, n) => sum + n, 0);
}

/** Per-herd attention rollup: a total plus a per-category breakdown (tooltips). */
export type HerdAttentionSummary = {
	total: number;
	byCategory: Partial<Record<LivestockAttentionCategory, number>>;
};

/**
 * The herd an attention item belongs to: herd-level items by their own
 * `entityId`, animal items by the `herdId` they carry. Returns undefined when
 * there is no herd context (e.g. an animal with no herd assignment).
 */
function herdKeyForItem(item: LivestockAttentionItem): string | undefined {
	if (item.entityType === "herd") return item.entityId;
	return item.herdId ?? undefined;
}

/**
 * Items per herd (herd-level items + animal items carrying herd context),
 * rolled up into a total and a per-category breakdown.
 */
export function attentionCountByHerd(
	items: LivestockAttentionItem[] | undefined,
): Map<string, HerdAttentionSummary> {
	const byHerd = new Map<string, HerdAttentionSummary>();
	for (const item of items ?? []) {
		const herdId = herdKeyForItem(item);
		if (!herdId) continue;
		const summary = byHerd.get(herdId) ?? { total: 0, byCategory: {} };
		summary.total += 1;
		summary.byCategory[item.category] =
			(summary.byCategory[item.category] ?? 0) + 1;
		byHerd.set(herdId, summary);
	}
	return byHerd;
}

/**
 * Human-readable per-category breakdown for a herd's attention badge, e.g.
 * "2 Vacinação pendente · 4 Tempo de pasto excedido". Categories with no count
 * are dropped; ordering follows LIVESTOCK_ATTENTION_CATEGORIES for stability.
 * Shared by the desktop badge tooltip and the mobile fallback lines so the
 * three surfaces never drift.
 */
export function formatAttentionBreakdown(
	byCategory: Partial<Record<LivestockAttentionCategory, number>> | undefined,
	t: TFunction,
): string {
	if (!byCategory) return "";
	return LIVESTOCK_ATTENTION_CATEGORIES.filter(
		({ key }) => (byCategory[key] ?? 0) > 0,
	)
		.map(
			({ key }) =>
				`${byCategory[key]} ${t(`livestock.attention.categories.${key}`)}`,
		)
		.join(" · ");
}

/** Days-per-year divisor for humanizing extreme durations (leap years ignored). */
const DAYS_PER_YEAR = 365;

/**
 * Humanized attention duration. Under a year it preserves the exact day-count
 * output each surface already used; at/above a year it collapses to "N+ anos"
 * so a "769 days overdue" badge stops training alarm fatigue.
 *
 * Two contexts, selected by `opts.overdue`:
 *  - overdue badge  → a full sentence ("N days overdue" / "N+ years overdue")
 *  - plain duration → just the count fragment for a detail line
 *                     (raw number / "N+ years"), e.g. the overstay day-count.
 */
export function formatAttentionDays(
	days: number,
	t: TFunction,
	opts?: { overdue?: boolean },
): string {
	if (days >= DAYS_PER_YEAR) {
		const years = Math.floor(days / DAYS_PER_YEAR);
		return opts?.overdue
			? t("livestock.attention.overdueYears", { count: years })
			: t("livestock.attention.yearsShort", { count: years });
	}
	return opts?.overdue
		? t("livestock.attention.overdueBy", { count: days })
		: String(days);
}

/**
 * Humanized duration WITH its unit ("32 days" / "2+ years") for sentence
 * templates like the overstay description — unlike `formatAttentionDays`,
 * whose plain variant returns a bare number and forces the template to
 * hardcode "days" (which broke when the value collapsed to "2+ years").
 */
export function formatAttentionDuration(days: number, t: TFunction): string {
	if (days >= DAYS_PER_YEAR) {
		return t("livestock.attention.yearsShort", {
			count: Math.floor(days / DAYS_PER_YEAR),
		});
	}
	return t("livestock.attention.daysCount", { count: days });
}

/**
 * Retired in the 2026-07-15 fact-grain redesign: entity pages now read exact
 * scoped slices from the backend (`livestockAttentionForHerdQuery` /
 * `livestockAttentionForAnimalQuery`) and list badges from
 * `livestockAttentionCountsQuery`, instead of re-deriving from the capped org
 * feed client-side. `attentionItemsForHerd` / `attentionItemsForAnimal` /
 * `attentionCountByAnimal` were removed.
 */
